import { Box } from "@mui/material";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "src/domains/shared/hooks/user";
import type {
  ManualChapterText,
  ManualSlots,
  ManualTopicText,
} from "./ManualChapterSection";
import type { ManualChapterId } from "./manualChapters";
import { ManualGuestPreview } from "./ManualGuestPreview";
import { ManualHero } from "./ManualHero";
import { ManualMemberBody } from "./ManualMemberBody";
import type { ManualSearchEntry } from "./manualSearchIndex";
import { MANUAL_STYLES } from "./manualStyles";
import { plainManualText } from "./ManualText";
import { hasChapterIntro, MANUAL_CHAPTERS } from "./manualTopics";

/** 누가 보는가 — 본문은 로그인한 사용자(`member`)에게만, 손님(`guest`)은 맛보기+안내, 확인 중(`checking`)은 첫 화면만 */
type ManualAccess = "checking" | "guest" | "member";

function manualAccess(isLoading: boolean, isSignedIn: boolean): ManualAccess {
  if (isLoading) return "checking";
  return isSignedIn ? "member" : "guest";
}

/**
 * 매뉴얼(`/manual`, service-manual): 첫 화면(검색·여덟 장) 아래 본문은 누가 보느냐에 따라 다르다 — 로그인한 사용자는
 * 여덟 장 전부(`ManualMemberBody`), 손님은 첫 절 맛보기와 로그인 안내(`ManualGuestPreview`, 「로그인하지 않은 방문자의
 * 매뉴얼」), 확인 중에는 자리만.
 */
export function ManualPage({ mappingGuideEntry }: ManualSlots) {
  const { t } = useTranslation("landing");
  const { currentUser, isLoading } = useAuth();
  const access = manualAccess(isLoading, Boolean(currentUser));
  /** 첫 화면의 여덟 장 줄 묶음 — 손님 안내 띠가 나타날 때를 잰다 */
  const chapterLinesRef = useRef<HTMLDivElement>(null);
  const chapters = t("site.manual.chapters", {
    returnObjects: true,
  }) as Readonly<Record<ManualChapterId, ManualChapterText>>;
  const topics = t("site.manual.topics", { returnObjects: true }) as Readonly<
    Record<string, ManualTopicText>
  >;

  const mappingText = [
    t("site.manual.mapping.gcpPath"),
    t("site.manual.mapping.autoDetect"),
    t("site.manual.mapping.ownerOnly"),
    t("site.manual.mapping.processingTime"),
  ];
  const searchEntries: ManualSearchEntry[] = MANUAL_CHAPTERS.flatMap(
    (chapter) =>
      chapter.topics.map((topic, topicIndex) => {
        const text = topics[topic.id];
        const body = [
          text.title,
          hasChapterIntro(chapter, topicIndex)
            ? (chapters[chapter.id].open ?? "")
            : "",
          ...(text.steps ?? []).flatMap((step) => [step.title, step.caption]),
          text.note ?? "",
          ...(topic.extra === "mapping" ? mappingText : []),
        ];
        return {
          id: topic.id,
          title: text.title,
          chapter: chapters[chapter.id].name,
          text: plainManualText(body.join(" ")),
        };
      })
  );

  const body = {
    checking: () => <Box sx={MANUAL_STYLES.checking} />,
    guest: () => (
      <ManualGuestPreview
        chapters={chapters}
        topics={topics}
        chapterLinesRef={chapterLinesRef}
      />
    ),
    member: () => (
      <ManualMemberBody
        chapters={chapters}
        topics={topics}
        mappingGuideEntry={mappingGuideEntry}
      />
    ),
  }[access];

  return (
    <Box
      sx={
        access === "guest"
          ? [MANUAL_STYLES.page, MANUAL_STYLES.guestPage]
          : MANUAL_STYLES.page
      }
    >
      <ManualHero
        searchEntries={searchEntries}
        interactive={access === "member"}
        chapterLinesRef={chapterLinesRef}
      />
      {body()}
    </Box>
  );
}
